import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { type RenderVariant, ToggleField } from '@atlassian/company-hub-common';

import { CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE } from '../../../../constants';
import type { UpdateMainFormFieldType } from '../../../../controllers/use-cards-extension-parameters-and-actions/types';
import type { CardsExtensionParameters } from '../../../../types';

import { messages } from './messages';

type AutoscrollToggleProps = {
	isAutoscrollEnabled: CardsExtensionParameters['isAutoscrollEnabled'];
	renderVariant: RenderVariant;
	updateMainFormField: UpdateMainFormFieldType;
};

export const AutoscrollToggle = ({
	isAutoscrollEnabled,
	renderVariant,
	updateMainFormField,
}: AutoscrollToggleProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleChange = useCallback(() => {
		updateMainFormField({ isAutoscrollEnabled: !isAutoscrollEnabled });

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'toggle',
				actionSubjectId: 'autoscrollToggle',
				source: CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
				attributes: {
					renderVariant,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isAutoscrollEnabled, renderVariant, updateMainFormField]);

	return (
		<ToggleField
			label={formatMessage(messages.autoscrollLabel)}
			Icon={
				<Tooltip content={formatMessage(messages.autoscrollTooltipContent)} position="bottom">
					<StatusInformationIcon
						testId="autoscroll-information-icon"
						label="autoscroll-information"
						color={token('color.icon.discovery')}
					/>
				</Tooltip>
			}
			isChecked={isAutoscrollEnabled}
			onChange={handleChange}
		/>
	);
};
